import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ILearner } from 'app/interfaces/learner.interface';
import { IClassroom } from 'app/interfaces/classroom.interface';
import { ISemester } from 'app/interfaces/semester';
import { ISeries } from 'app/interfaces/series';
import { LearnersService } from 'app/services/learners/learners.service';
import { ClassroomService } from 'app/services/classroom/classroom.service';
import { SemesterService } from 'app/services/semester/semester.service';
import { SeriesService } from 'app/services/series/series.service';
import { SchoolContextService } from 'app/services/school/school-context.service';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-transfer-learner-class-dialog',
  templateUrl: './transfer-learner-class-dialog.component.html',
  styleUrls: ['./transfer-learner-class-dialog.component.scss']
})
export class TransferLearnerClassDialogComponent implements OnChanges {
  @Input() visible = false;
  @Input() learner: ILearner;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() transferred = new EventEmitter<void>();

  transferForm: FormGroup;
  targetClassrooms: IClassroom[] = [];
  targetSeriesList: ISeries[] = [];
  semesters: ISemester[] = [];
  isSubmitting = false;
  isUniversity = false;

  constructor(
    private fb: FormBuilder,
    private learnersService: LearnersService,
    private classroomService: ClassroomService,
    private semesterService: SemesterService,
    private seriesService: SeriesService,
    private schoolContextService: SchoolContextService,
    private messageService: MessageService,
  ) {
    this.transferForm = this.fb.group({
      target_classroom_id: [null],
      target_series_id: [null],
      effective_semester_id: [null, Validators.required],
      reason: [''],
    });
    this.isUniversity = this.schoolContextService.isUniversity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue && this.learner) {
      this.loadOptions();
    }
  }

  get currentClassLabel(): string {
    return this.learner?.classroom?.name || 'Not assigned';
  }

  get currentSeriesLabel(): string {
    return this.learner?.series?.name || 'Not assigned';
  }

  private loadOptions(): void {
    this.semesterService.allSemesters().subscribe({
      next: (res) => {
        this.semesters = res?.data || [];
      },
      error: () => {
        this.semesters = [];
      }
    });

    this.classroomService.allClassrooms().subscribe({
      next: (res) => {
        const all: IClassroom[] = res?.data || [];
        const levelId = this.learner?.academic_level?.id;
        const currentId = this.learner?.classroom?.id;
        this.targetClassrooms = all.filter(
          (c) =>
            (!levelId || c.academic_level?.id === levelId) &&
            c.id !== currentId
        );
      },
      error: () => {
        this.targetClassrooms = [];
      }
    });

    if (this.isUniversity) {
      this.seriesService.allSeries().subscribe({
        next: (res) => {
          const all: ISeries[] = res?.data || [];
          const currentSeriesId = this.learner?.series?.id;
          this.targetSeriesList = all.filter((s) => s.id !== currentSeriesId);
        },
        error: () => {
          this.targetSeriesList = [];
        }
      });
    }
  }

  onHide(): void {
    this.visibleChange.emit(false);
    this.transferForm.reset();
  }

  submit(): void {
    if (!this.learner?.id || this.transferForm.invalid) {
      this.transferForm.markAllAsTouched();
      return;
    }

    const values = this.transferForm.value;
    if (!values.target_classroom_id && !values.target_series_id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Select a target class or specialty',
      });
      return;
    }

    this.isSubmitting = true;
    this.learnersService.transferLearnerClass({
      learner_id: this.learner.id,
      target_classroom_id: values.target_classroom_id || undefined,
      target_series_id: values.target_series_id || undefined,
      effective_semester_id: values.effective_semester_id,
      reason: values.reason || undefined,
    }).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Transfer complete',
          detail: 'Learner has been moved. Historical records are preserved.',
        });
        this.isSubmitting = false;
        this.transferred.emit();
        this.onHide();
      },
      error: (err) => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Transfer failed',
          detail: err?.error?.message || err?.message || 'Could not transfer learner',
        });
      }
    });
  }
}
